import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API, resolveImg, imgFallback, inr, inrFull, num, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MapPin, ShieldCheck, FileArrowDown, Handshake, SealCheck, Package, Eye, Lock, Heart, Phone, X, CaretLeft, CaretRight, MagnifyingGlassPlus, Play } from "@phosphor-icons/react";
import { lotCode, brandTypeClass, SUPPORT_PHONE, SUPPORT_PHONE_TEL } from "@/lib/config";

function Stat({ label, value, accent }) {
  return (
    <div className="border-r border-b border-border/60 p-5 last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className={`stat mt-1 text-lg font-bold ${accent ? "text-brand" : "text-navy"}`}>{value}</div>
    </div>
  );
}

export default function LotDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lot, setLot] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [eoiOpen, setEoiOpen] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [eoi, setEoi] = useState({ indicative_price: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);

  useEffect(() => {
    api.get(`/lots/${id}`).then(({ data }) => setLot(data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (user?.role === "buyer") {
      api.get("/buyer/shortlist").then(({ data }) => setShortlisted(data.ids.includes(id))).catch(() => {});
    }
  }, [id, user]);

  useEffect(() => {
    if (!lightbox || !lot) return;
    const total = (lot.image_ids || []).length + (lot.video_ids || []).length;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowRight" && total > 1) setActive((a) => (a + 1) % total);
      else if (e.key === "ArrowLeft" && total > 1) setActive((a) => (a - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, lot]);

  const requireLogin = (next) => {
    if (!user) { nav("/login", { state: { from: `/lots/${id}` } }); return false; }
    return true;
  };

  const toggleShortlist = async () => {
    if (!requireLogin()) return;
    if (user.role !== "buyer") { toast.error("Only buyer accounts can shortlist lots"); return; }
    try {
      const { data } = await api.post(`/buyer/shortlist/${id}`);
      setShortlisted(data.shortlisted);
      toast.success(data.shortlisted ? "Added to your shortlist" : "Removed from shortlist");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDownload = async () => {
    try {
      const token = localStorage.getItem("sh_token");
      const res = await fetch(`${API}/files/${lot.sanitized_excel_file_id}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `SparesHub_${lot.title.slice(0, 24)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sanitized inventory downloaded");
      setDlOpen(false);
    } catch (e) {
      toast.error("Could not download file. Please try again.");
    }
  };

  const submitEoi = async () => {
    if (!requireLogin()) return;
    setSubmitting(true);
    try {
      await api.post("/eois", { lot_id: lot.id, indicative_price: Number(eoi.indicative_price || 0), notes: eoi.notes });
      toast.success("Quote submitted — our team will contact you shortly");
      setEoiOpen(false);
      setEoi({ indicative_price: "", notes: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <Package size={48} className="mx-auto text-navy/20" />
      <h1 className="mt-4 font-display text-2xl font-bold text-navy">Lot not available</h1>
      <p className="mt-2 text-muted-foreground">This lot may have been sold, closed, or suspended.</p>
      <Button asChild className="mt-6 rounded-sm bg-navy text-white"><Link to="/lots">Back to all lots</Link></Button>
    </div>
  );
  if (!lot) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-navy/20 border-t-brand" /></div>;

  const images = (lot.image_ids || []).map(resolveImg).filter(Boolean);
  const videos = (lot.video_ids || []).map(resolveImg).filter(Boolean);
  const media = [...images.map((url) => ({ type: "image", url })), ...videos.map((url) => ({ type: "video", url }))];
  const discount = lot.mrp_value ? Math.round((1 - lot.asking_price / lot.mrp_value) * 100) : 0;

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
      <Link to="/lots" className="text-sm text-muted-foreground hover:text-brand">← Back to lots</Link>

      <div className="mt-4 grid gap-10 lg:grid-cols-12">
        {/* Gallery */}
        <div className="min-w-0 lg:col-span-4">
          <button type="button" onClick={() => media[active] && setLightbox(true)} className="group relative block w-full overflow-hidden rounded-sm border border-border bg-navy-50" data-testid="gallery-main">
            <div className="aspect-[4/3]">
              {media[active] ? (
                media[active].type === "video" ? (
                  <div className="relative flex h-full w-full items-center justify-center bg-black">
                    <video src={media[active].url} muted playsInline preload="metadata" className="h-full w-full object-contain" />
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/90 text-white shadow-lg"><Play weight="fill" size={26} /></span>
                    </span>
                  </div>
                ) : (
                  <img src={media[active].url} onError={imgFallback} alt={lot.title} className="h-full w-full cursor-zoom-in object-cover" />
                )
              ) : (
                <div className="flex h-full items-center justify-center"><Package size={48} className="text-navy/20" /></div>
              )}
            </div>
            {media[active] && (
              <span className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-navy/80 px-2 py-1 text-[11px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
                {media[active].type === "video" ? <><Play weight="fill" size={12} /> Click to play</> : <><MagnifyingGlassPlus size={13} /> Click to zoom</>}
              </span>
            )}
          </button>
          {media.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {media.map((m, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`relative h-16 w-24 overflow-hidden rounded-sm border-2 ${active === i ? "border-brand" : "border-transparent"}`}>
                  {m.type === "video" ? (
                    <div className="flex h-full w-full items-center justify-center bg-navy">
                      <Play weight="fill" size={18} className="text-white" />
                      <span className="absolute bottom-0.5 right-0.5 rounded-sm bg-black/60 px-1 text-[8px] font-semibold uppercase text-white">Video</span>
                    </div>
                  ) : (
                    <img src={m.url} onError={imgFallback} alt="" className="h-full w-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-8">
          <div className="sticky top-20 space-y-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                {lot.vehicle_type && <span className="rounded-sm bg-navy px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white" data-testid="detail-vehicle-type">{lot.vehicle_type}</span>}
                {lot.brand_type && <span className={`rounded-sm px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${brandTypeClass(lot.brand_type)}`} data-testid="detail-brand-type">{lot.brand_type}</span>}
                {lot.category && <span className="rounded-sm border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-subtext">{lot.category}</span>}
                {lot.featured && <span className="rounded-sm bg-brand px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">Featured</span>}
                <span className="inline-flex items-center gap-1 rounded-md border border-teal/40 bg-teal/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-darkteal"><SealCheck weight="fill" size={12} /> Verified</span>
              </div>
              <h1 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight text-navy lg:text-3xl">{lot.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="stat font-semibold text-navy" data-testid="detail-lot-code">LOT {lotCode(lot)}</span>
                {(lot.city || lot.state) && <span className="flex items-center gap-1"><MapPin size={14} /> {[lot.city, lot.state].filter(Boolean).join(", ")}</span>}
                <span className="flex items-center gap-1"><Eye size={14} /> {num(lot.views)} views</span>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-white">
              <div className="flex items-end justify-between border-b border-border p-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Asking price</div>
                  <div className="stat text-3xl font-black text-brand">{inr(lot.asking_price)}</div>
                </div>
                {discount > 0 && <span className="stat rounded-md bg-brand/10 px-2 py-1 text-sm font-bold text-brand">{discount}% off MRP</span>}
              </div>
              <div className="grid grid-cols-2">
                <Stat label="Total MRP Value" value={inr(lot.mrp_value)} />
                <Stat label="Total Units" value={num(lot.total_units)} />
                <Stat label="Unique SKUs" value={num(lot.unique_skus)} />
                {lot.condition && <Stat label="Parts Condition" value={lot.condition} />}
                {lot.packaging_condition && <Stat label="Packaging Condition" value={lot.packaging_condition} />}
              </div>
            </div>

            <div className="space-y-3">
              <Button onClick={() => (user ? setEoiOpen(true) : nav("/login", { state: { from: `/lots/${id}` } }))} className="h-12 w-full rounded-sm bg-brand text-base font-semibold text-white hover:bg-brand-hover" data-testid="express-interest-button">
                <Handshake className="mr-1" /> Submit Your Quote
              </Button>
              <Button onClick={() => setDlOpen(true)} variant="outline" className="h-12 w-full rounded-sm border-navy/20 text-base font-semibold text-navy hover:bg-navy hover:text-white" data-testid="download-excel-button">
                <FileArrowDown className="mr-1" /> Download Sanitized Excel
              </Button>
              <Button onClick={toggleShortlist} variant="ghost" className={`h-11 w-full rounded-sm text-base font-semibold ${shortlisted ? "text-brand hover:bg-brand/5" : "text-navy hover:bg-navy/5"}`} data-testid="shortlist-button">
                <Heart weight={shortlisted ? "fill" : "regular"} className="mr-1" /> {shortlisted ? "Shortlisted" : "Add to shortlist"}
              </Button>
              {!user && <p className="text-center text-xs text-muted-foreground">Login required to submit your quote</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Description + sample inventory (full width) */}
      <div className="mt-12 max-w-4xl">
        <div>
          <h2 className="font-display text-xl font-bold text-navy">Lot description</h2>
          <p className="mt-3 leading-relaxed text-muted-foreground">{lot.description}</p>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-navy">Sample inventory</h2>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Preview · {num(lot.unique_skus)} total SKUs</span>
          </div>
          <div className="mt-3 overflow-x-auto rounded-sm border border-border">
            <table className="w-full min-w-[500px] text-left text-sm">
              <thead className="bg-navy text-white text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-2.5">SKU</th><th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5 text-right">Qty</th><th className="px-4 py-2.5 text-right">Unit MRP</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(lot.sample_inventory || []).map((r, i) => (
                  <tr key={i} className="bg-white">
                    <td className="stat px-4 py-2.5 text-navy">{r.sku}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.description}</td>
                    <td className="stat px-4 py-2.5 text-right">{num(r.qty)}</td>
                    <td className="stat px-4 py-2.5 text-right">{inrFull(r.mrp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-sm border border-dashed border-border bg-navy-50 p-3 text-sm text-muted-foreground">
            <FileArrowDown size={16} className="text-brand" /> Download the full sanitized inventory using the Download button above.
          </div>
        </div>
      </div>

      {/* Trust + contact + seller (below content) */}
      <div className="mt-8 grid max-w-4xl gap-4 md:grid-cols-3">
        <div className="flex items-start gap-3 rounded-xl border border-teal/30 bg-darkteal-50 p-4">
          <ShieldCheck weight="fill" className="mt-0.5 h-6 w-6 shrink-0 text-darkteal" />
          <div>
            <div className="text-sm font-semibold text-ink">Escrow-protected transaction</div>
            <p className="mt-1 text-xs text-subtext">SparesHub manages negotiation and holds funds in escrow. Release happens only after you inspect and accept the lot.</p>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-white p-4 text-sm">
          <div className="flex items-center gap-2 font-semibold text-ink"><Phone size={16} className="text-brand" /> Talk to SparesHub about this lot</div>
          <a href={`tel:${SUPPORT_PHONE_TEL}`} className="stat mt-1 block text-lg font-bold text-navy hover:text-brand" data-testid="lot-contact-phone">{SUPPORT_PHONE}</a>
          <div className="mt-1 text-xs text-muted-foreground">Quote lot number <span className="stat font-semibold text-navy">{lotCode(lot)}</span> when you call.</div>
        </div>

        <div className="rounded-xl border border-border bg-navy-50 p-4 text-sm">
          <div className="text-muted-foreground">Seller ID</div>
          <div className="stat text-base font-bold text-ink">{lot.seller_ref || "SPARESHUB-VERIFIED"}</div>
          <div className="mt-1 text-xs text-muted-foreground">Seller identity is kept confidential and disclosed only after a deal is agreed.</div>
        </div>
      </div>

      {/* EOI dialog */}
      <Dialog open={eoiOpen} onOpenChange={setEoiOpen}>
        <DialogContent className="rounded-sm" data-testid="eoi-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Submit your quote for this lot</DialogTitle>
            <DialogDescription>Share your indicative price and notes. This is visible only to the SparesHub team — never to other buyers or the seller.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Your indicative price (₹)</Label>
              <Input type="number" data-testid="eoi-price" value={eoi.indicative_price} onChange={(e) => setEoi({ ...eoi, indicative_price: e.target.value })} placeholder="e.g. 4200000" className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label>Notes for our sales team</Label>
              <Textarea data-testid="eoi-notes" value={eoi.notes} onChange={(e) => setEoi({ ...eoi, notes: e.target.value })} placeholder="Inspection timeline, partial-lot interest, logistics needs..." className="mt-1.5 rounded-sm" rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitEoi} disabled={submitting} className="rounded-sm bg-brand text-white hover:bg-brand-hover" data-testid="eoi-submit">{submitting ? "Submitting..." : "Submit quote"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download dialog */}
      <Dialog open={dlOpen} onOpenChange={setDlOpen}>
        <DialogContent className="rounded-sm" data-testid="download-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Download sanitized inventory</DialogTitle>
            <DialogDescription>You are downloading the SparesHub-sanitized buyer-facing file. Seller identity and cost data have been removed. This download is logged for compliance.</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-sm border border-border bg-navy-50 p-4 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-ok" /> By downloading, you agree to use this data solely to evaluate a bulk purchase through SparesHub.
          </div>
          <DialogFooter>
            <Button onClick={doDownload} className="rounded-sm bg-navy text-white hover:bg-navy-700" data-testid="confirm-download"><FileArrowDown className="mr-1" /> Download file</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media lightbox */}
      {lightbox && media[active] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(false)} data-testid="image-lightbox">
          {media[active].type === "video" ? (
            <video src={media[active].url} controls autoPlay playsInline className="max-h-[90vh] max-w-[92vw] rounded-md bg-black" onClick={(e) => e.stopPropagation()} data-testid="lightbox-video" />
          ) : (
            <img src={media[active].url} onError={imgFallback} alt={lot.title} className="max-h-[90vh] max-w-[92vw] rounded-md object-contain" onClick={(e) => e.stopPropagation()} />
          )}
          <button onClick={() => setLightbox(false)} className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25" data-testid="lightbox-close"><X size={22} /></button>
          {media.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setActive((active - 1 + media.length) % media.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/25" data-testid="lightbox-prev"><CaretLeft size={24} /></button>
              <button onClick={(e) => { e.stopPropagation(); setActive((active + 1) % media.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/25" data-testid="lightbox-next"><CaretRight size={24} /></button>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white" data-testid="lightbox-counter">{active + 1} / {media.length}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
